import { useState, useCallback, useEffect } from 'react';
import translations from '../translations/messages';

export const useTranslate = (initialLocale = 'en') => {
  const [locale, setLocale] = useState(() => {
    return localStorage.getItem('locale') || initialLocale;
  });
  
  const [translationsCache, setTranslationsCache] = useState({});
  
  useEffect(() => {
    document.documentElement.dir = ['ar', 'he', 'ur', 'fa'].includes(locale) ? 'rtl' : 'ltr';
    document.body.classList.toggle('rtl-layout', ['ar', 'he', 'ur', 'fa'].includes(locale));
    localStorage.setItem('locale', locale);
  }, [locale]);

  const t = useCallback(
    (key, defaultText) => {
      if (locale === 'en') return defaultText;
      
      if (translationsCache[key]) return translationsCache[key];
      
      if (translations[locale] && translations[locale][key]) {
        setTranslationsCache(prev => ({
          ...prev,
          [key]: translations[locale][key]
        }));
        return translations[locale][key];
      }
      
      return defaultText;
    },
    [locale, translationsCache]
  );

  const changeLanguage = useCallback((newLocale) => {
    setLocale(newLocale);
    setTranslationsCache({});
  }, []);

  const tWithFallback = useCallback(
    async (key, defaultText) => {
      const staticTranslation = t(key, null);
      if (staticTranslation) return staticTranslation;
      
      if (locale !== 'en') {
        try {
          const res = await fetch("https://libretranslate.com/translate", {
            method: "POST",
            body: JSON.stringify({
              q: defaultText,
              source: 'en',
              target: locale,
              format: "text",
            }),
            headers: { "Content-Type": "application/json" },
          });
          
          const data = await res.json();
          const translatedText = data.translatedText || defaultText;
          
          setTranslationsCache(prev => ({
            ...prev,
            [key]: translatedText
          }));
          
          return translatedText;
        } catch (error) {
          return defaultText;
        }
      }
      
      return defaultText;
    },
    [locale, t]
  );

  return { 
    t,
    tWithFallback,
    locale, 
    changeLanguage
  };
};
